/**
 * CR201: Safe Asset Image Component
 * 
 * Renders assets (SVG, PNG, etc.) via <img> tag instead of dangerouslySetInnerHTML.
 * This eliminates XSS risk from database-stored SVG markup.
 * 
 * Resolution order:
 * 1. url (public storage URL) - preferred
 * 2. svg (inline markup) - converted to data URI, legacy support only
 * 
 * Eventually the svg path will be removed once all assets are migrated to storage.
 */

import { useState, useMemo } from "react";
import { getAppAssetPublicUrl } from "@/lib/assets/getAppAssetPublicUrl";
import { cn } from "@/lib/utils";

interface SafeAssetImageProps {
  /** Public URL of the asset (preferred) */
  url?: string | null;
  /** Inline SVG markup (deprecated - for migration period only) */
  svg?: string | null;
  /** Alt text for accessibility */
  alt: string;
  /** CSS class names */
  className?: string;
  /** Fallback content when no image available */
  fallback?: React.ReactNode;
  /** Called when image fails to load */
  onError?: () => void;
  /** Called when image loads successfully */
  onLoad?: () => void;
  /** Style object */
  style?: React.CSSProperties;
}

/**
 * Convert SVG markup to a safe data URI
 * This is a temporary bridge for legacy inline SVG while migrating to storage URLs
 */
function svgToDataUri(svg: string): string {
  // Encode the SVG for use in a data URI
  const encoded = encodeURIComponent(svg)
    .replace(/'/g, "%27")
    .replace(/"/g, "%22");
  return `data:image/svg+xml,${encoded}`;
}

export function SafeAssetImage({
  url,
  svg,
  alt,
  className,
  fallback,
  onError,
  onLoad,
  style,
}: SafeAssetImageProps) {
  const [hasError, setHasError] = useState(false);

  // CR282: Determine the image source, resolving storage keys to full URLs
  const src = useMemo(() => {
    // Prefer URL (storage-based assets) - resolve key to full URL if needed
    if (url) {
      const resolvedUrl = getAppAssetPublicUrl({ url });
      if (resolvedUrl) return resolvedUrl;
    }
    
    // Fall back to converting inline SVG to data URI (legacy support)
    if (svg) return svgToDataUri(svg);
    
    return null;
  }, [url, svg]);

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  const handleLoad = () => {
    setHasError(false);
    onLoad?.();
  };

  // No source available - show fallback
  if (!src || hasError) {
    return fallback ? <>{fallback}</> : null;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={cn("object-contain", className)}
      style={style}
      onError={handleError}
      onLoad={handleLoad}
      // Prevent dragging of asset images
      draggable={false}
    />
  );
}

/**
 * Hook to get asset image props from an AppAsset object
 */
export function useAssetImageProps(asset: { url?: string | null; svg?: string | null; name: string } | null) {
  return useMemo(() => {
    if (!asset) return null;
    return {
      url: asset.url,
      svg: asset.svg,
      alt: asset.name,
    };
  }, [asset]);
}
